import { useEffect, useRef, useState } from "react";
import { Canvas as FabricCanvas, FabricText, Circle, Rect, loadSVGFromString, FabricImage, util } from "fabric";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { 
  Type, 
  Circle as CircleIcon, 
  Square, 
  Palette,
  Move,
  Save,
  Undo,
  Redo,
  Trash2,
  ZoomIn,
  ZoomOut
} from "lucide-react";
import { toast } from "sonner";

interface VisualEditorProps {
  svgContent: string;
  onSave: (svgContent: string, version?: string) => void;
  onCancel: () => void;
}

// Helper function to load SVG as background image when fabric.js can't parse it
const loadSvgAsBackground = async (canvas: FabricCanvas, svgContent: string) => {
  try {
    console.log("Loading SVG as background image...");
    
    // Convert SVG to data URL
    const svgBlob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(svgBlob);
    
    // Use Fabric.js v6 API to create image from URL
    const fabricImg = await FabricImage.fromURL(url, {
      crossOrigin: 'anonymous'
    });
    
    console.log("Image created from SVG:", fabricImg);
    
    // Scale to fit canvas with proper positioning
    const canvasWidth = canvas.getWidth();
    const canvasHeight = canvas.getHeight();
    
    // Calculate optimal scale to fit within canvas
    const scaleX = (canvasWidth * 0.95) / fabricImg.width;
    const scaleY = (canvasHeight * 0.95) / fabricImg.height;
    const scale = Math.min(scaleX, scaleY);
    
    // Calculate centered position
    const scaledWidth = fabricImg.width * scale;
    const scaledHeight = fabricImg.height * scale;
    const left = Math.max(0, (canvasWidth - scaledWidth) / 2);
    const top = Math.max(0, (canvasHeight - scaledHeight) / 2);
    
    fabricImg.set({
      left: left,
      top: top,
      scaleX: scale,
      scaleY: scale,
      selectable: false, // Make it a background
      evented: false,
      lockMovementX: true,
      lockMovementY: true
    });
    
    canvas.add(fabricImg);
    canvas.sendObjectToBack(fabricImg); // Send to back so it acts as background
    canvas.renderAll();
    
    toast.success("SVG loaded as background image. You can add new elements on top!");
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error in loadSvgAsBackground:', error);
    toast.error("Complex SVG detected. You can still add new elements, or try using 'Edit by description' mode instead.");
    
    // Add a simple text element as a starting point
    const welcomeText = new FabricText("Complex SVG loaded as background.\nAdd new elements using the toolbar!", {
      left: 50,
      top: 50,
      fill: '#333333',
      fontSize: 16,
      fontFamily: "Arial",
      textAlign: 'center'
    });
    canvas.add(welcomeText);
    canvas.renderAll();
  }
};

export const VisualEditor = ({ svgContent, onSave, onCancel }: VisualEditorProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [fabricCanvas, setFabricCanvas] = useState<FabricCanvas | null>(null);
  const [activeTool, setActiveTool] = useState<"select" | "text" | "circle" | "rectangle">("select");
  const [selectedColor, setSelectedColor] = useState("#000000");
  const [selectedObject, setSelectedObject] = useState<any>(null);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [version, setVersion] = useState(1);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Determine canvas size from SVG if available
    let initialWidth = 600;
    let initialHeight = 400;
    try {
      if (svgContent) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(svgContent, 'image/svg+xml');
        const svgEl = doc.querySelector('svg');
        if (svgEl) {
          const vw = svgEl.getAttribute('viewBox');
          const wAttr = svgEl.getAttribute('width');
          const hAttr = svgEl.getAttribute('height');
          const parseNum = (v: string | null) => {
            if (!v) return NaN;
            const m = v.match(/([0-9.]+)/);
            return m ? parseFloat(m[1]) : NaN;
          };
          let w = parseNum(wAttr);
          let h = parseNum(hAttr);
          if ((!w || !h) && vw) {
            const parts = vw.split(/\s+/).map(Number);
            if (parts.length === 4) {
              w = parts[2];
              h = parts[3];
            }
          }
          if (w && h) {
            initialWidth = Math.min(Math.max(Math.round(w), 400), 1400);
            initialHeight = Math.min(Math.max(Math.round(h), 300), 1200);
          }
        }
      }
    } catch (e) {
      console.warn('Could not parse SVG size:', e);
    }

    const canvas = new FabricCanvas(canvasRef.current, {
      width: initialWidth,
      height: initialHeight,
      backgroundColor: "#ffffff",
    });

    // Parse SVG and load it into canvas
    if (svgContent) {
      console.log('Loading SVG content:', svgContent?.substring(0, 200) + '...');
      
      // First try to load as fabric objects
      try {
        loadSVGFromString(svgContent).then((result) => {
          console.log('SVG loaded successfully, objects found:', result.objects.length);
          if (result.objects && result.objects.length > 0) {
            // Clear existing content first
            canvas.clear();
            canvas.backgroundColor = "#ffffff";
            
            // Add objects with proper positioning
            result.objects.forEach((obj) => {
              // Ensure objects are positioned within canvas bounds
              if (obj.left < 0) obj.left = 10;
              if (obj.top < 0) obj.top = 10;
              if (obj.left > canvas.getWidth() - 50) obj.left = canvas.getWidth() - 50;
              if (obj.top > canvas.getHeight() - 50) obj.top = canvas.getHeight() - 50;
              
              canvas.add(obj);
            });
            
            // Fit all objects in view
            canvas.renderAll();
            saveToHistory(canvas);
            toast.success(`Visual loaded! Found ${result.objects.length} objects to edit.`);
          } else {
            console.warn('No editable objects found in SVG, loading as background image');
            // If no objects found, try to load SVG as background image
            loadSvgAsBackground(canvas, svgContent);
          }
        }).catch((error) => {
          console.error('Error loading SVG:', error);
          // Fallback to background image
          loadSvgAsBackground(canvas, svgContent);
        });
      } catch (syncError) {
        console.error('Synchronous error loading SVG:', syncError);
        // Fallback to background image
        loadSvgAsBackground(canvas, svgContent);
      }
    } else {
      console.log('No SVG content provided to Visual Editor');
      toast.info("No visual content to load - you can start creating new content!");
    }

    canvas.on('selection:created', (e) => {
      setSelectedObject(e.selected?.[0] || null);
    });

    canvas.on('selection:updated', (e) => {
      setSelectedObject(e.selected?.[0] || null);
    });

    canvas.on('selection:cleared', () => {
      setSelectedObject(null);
    });

    canvas.on('object:modified', () => {
      saveToHistory(canvas);
    });

    // Enable double-click to edit text directly on canvas
    canvas.on('mouse:dblclick', (e) => {
      const target = e.target;
      if (target && target.type === 'text') {
        // Fabric.js v6 should handle text editing automatically for editable text objects
        target.set('isEditing', true);
        canvas.setActiveObject(target);
        canvas.renderAll();
      }
    });

    // Handle text editing events
    canvas.on('text:editing:entered', () => {
      // Text editing started
    });

    canvas.on('text:editing:exited', (e) => {
      // Text editing ended, save to history
      saveToHistory(canvas);
    });

    setFabricCanvas(canvas);
    toast("Visual editor ready! Start editing your content.");

    return () => {
      canvas.dispose();
    };
  }, [svgContent]);

  const saveToHistory = (canvas: FabricCanvas) => {
    const state = JSON.stringify(canvas.toJSON());
    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(state);
      return newHistory.slice(-20); // Keep last 20 states
    });
    setHistoryIndex(prev => Math.min(prev + 1, 19));
  };

  const undo = () => {
    if (!fabricCanvas || historyIndex <= 0) return;
    
    const newIndex = historyIndex - 1;
    fabricCanvas.loadFromJSON(history[newIndex], () => {
      fabricCanvas.renderAll();
      setHistoryIndex(newIndex);
    });
  };

  const redo = () => {
    if (!fabricCanvas || historyIndex >= history.length - 1) return;
    
    const newIndex = historyIndex + 1;
    fabricCanvas.loadFromJSON(history[newIndex], () => {
      fabricCanvas.renderAll();
      setHistoryIndex(newIndex);
    });
  };

  const handleToolClick = (tool: typeof activeTool) => {
    if (!fabricCanvas) return;
    
    setActiveTool(tool);
    fabricCanvas.isDrawingMode = false;

    if (tool === "text") {
      const text = new FabricText("Double-click to edit\nPress Enter for new lines", {
        left: 100,
        top: 100,
        fill: selectedColor,
        fontSize: 20,
        fontFamily: "Arial",
        textAlign: 'left',
        splitByGrapheme: false, // Better text handling
        editable: true, // Make text editable
        cursorWidth: 2,
        cursorColor: selectedColor
      });
      fabricCanvas.add(text);
      fabricCanvas.setActiveObject(text);
      saveToHistory(fabricCanvas);
    } else if (tool === "rectangle") {
      const rect = new Rect({
        left: 100,
        top: 100,
        fill: selectedColor,
        width: 100,
        height: 100,
      });
      fabricCanvas.add(rect);
      saveToHistory(fabricCanvas);
    } else if (tool === "circle") {
      const circle = new Circle({
        left: 100,
        top: 100,
        fill: selectedColor,
        radius: 50,
      });
      fabricCanvas.add(circle);
      saveToHistory(fabricCanvas);
    }
  };

  const deleteSelected = () => {
    if (!fabricCanvas) return;
    
    const activeObjects = fabricCanvas.getActiveObjects();
    if (activeObjects.length) {
      fabricCanvas.remove(...activeObjects);
      fabricCanvas.discardActiveObject();
      saveToHistory(fabricCanvas);
      toast("Selected objects deleted");
    }
  };

  const updateSelectedObjectColor = (color: string) => {
    if (!fabricCanvas || !selectedObject) return;
    
    selectedObject.set('fill', color);
    fabricCanvas.renderAll();
    saveToHistory(fabricCanvas);
  };

  const updateSelectedObjectText = (text: string) => {
    if (!fabricCanvas || !selectedObject || selectedObject.type !== 'text') return;
    
    // Handle line breaks properly by setting the text directly
    selectedObject.set({
      text: text,
      splitByGrapheme: false
    });
    fabricCanvas.renderAll();
    saveToHistory(fabricCanvas);
  };

  const zoomIn = () => {
    if (!fabricCanvas) return;
    const newZoom = Math.min(zoomLevel * 1.2, 3);
    setZoomLevel(newZoom);
    fabricCanvas.setZoom(newZoom);
    fabricCanvas.renderAll();
  };

  const zoomOut = () => {
    if (!fabricCanvas) return;
    const newZoom = Math.max(zoomLevel / 1.2, 0.3);
    setZoomLevel(newZoom);
    fabricCanvas.setZoom(newZoom);
    fabricCanvas.renderAll();
  };

  const handleSave = () => {
    if (!fabricCanvas) return;
    
    // Export to SVG and embed Font Awesome CSS so icons render correctly everywhere
    let svgData = fabricCanvas.toSVG();

    // Inject Font Awesome styles into the SVG (self-contained)
    const faStyle = `\n  <defs>\n    <style><![CDATA[\n      @import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css');\n      /* Make FA available; normal text will fall back to system fonts if glyph missing */\n      text, tspan {\n        font-family: 'Font Awesome 6 Free', 'Font Awesome 6 Brands', system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;\n      }\n      /* Solid weight for icons; normal letters will fallback to system font */\n      text, tspan { font-weight: 900; }\n    ]]></style>\n  </defs>\n`;

    if (svgData.includes('<svg')) {
      svgData = svgData.replace(/<svg([^>]*)>/, (m) => `${m}\n${faStyle}`);
    }

    const versionString = `v${version}`;
    onSave(svgData, versionString);
    setVersion(prev => prev + 1);
    toast(`Visual content saved as ${versionString}!`);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-2 p-4 border-b border-border">
        <div className="flex items-center gap-1">
          <Button
            variant={activeTool === "select" ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveTool("select")}
          >
            <Move className="w-4 h-4" />
          </Button>
          <Button
            variant={activeTool === "text" ? "default" : "outline"}
            size="sm"
            onClick={() => handleToolClick("text")}
          >
            <Type className="w-4 h-4" />
          </Button>
          <Button
            variant={activeTool === "rectangle" ? "default" : "outline"}
            size="sm"
            onClick={() => handleToolClick("rectangle")}
          >
            <Square className="w-4 h-4" />
          </Button>
          <Button
            variant={activeTool === "circle" ? "default" : "outline"}
            size="sm"
            onClick={() => handleToolClick("circle")}
          >
            <CircleIcon className="w-4 h-4" />
          </Button>
        </div>

        <Separator orientation="vertical" className="h-6" />

        <div className="flex items-center gap-2">
          <Label htmlFor="color-picker" className="text-sm">Color:</Label>
          <Input
            id="color-picker"
            type="color"
            value={selectedColor}
            onChange={(e) => setSelectedColor(e.target.value)}
            className="w-12 h-8 p-0 border-0"
          />
        </div>

        <Separator orientation="vertical" className="h-6" />

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={zoomOut}
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground min-w-[3rem] text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={zoomIn}
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>

        <Separator orientation="vertical" className="h-6" />

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="sm"
            onClick={undo}
            disabled={historyIndex <= 0}
          >
            <Undo className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
          >
            <Redo className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={deleteSelected}
            disabled={!selectedObject}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Save className="w-4 h-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Canvas Area */}
        <div className="flex-1 p-4 bg-muted/5">
          <div className="border border-border rounded-lg overflow-hidden bg-white shadow-sm">
            <canvas ref={canvasRef} className="block" />
          </div>
        </div>

        {/* Properties Panel */}
        <div className="w-64 border-l border-border p-4 space-y-4 overflow-y-auto">
          <div>
            <Label className="text-sm font-medium">Properties</Label>
            {selectedObject ? (
              <div className="mt-2 space-y-3">
                <Badge variant="secondary" className="text-xs">
                  {selectedObject.type || 'Object'} selected
                </Badge>
                
                {selectedObject.type === 'text' && (
                  <div>
                    <Label htmlFor="text-content" className="text-xs">Text Content</Label>
                    <Textarea
                      id="text-content"
                      value={selectedObject.text || ''}
                      onChange={(e) => updateSelectedObjectText(e.target.value)}
                      className="mt-1 min-h-[80px] resize-none"
                      placeholder="Enter text here...&#10;Use Enter for new lines"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Press Enter to add line breaks
                    </p>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="object-color" className="text-xs">Fill Color</Label>
                  <Input
                    id="object-color"
                    type="color"
                    value={selectedObject.fill || '#000000'}
                    onChange={(e) => updateSelectedObjectColor(e.target.value)}
                    className="mt-1 w-full h-8 p-0 border-0"
                  />
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground mt-2">
                Select an object to edit its properties
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};